import { useState, useEffect, useRef } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import { ReportStepper, type Step } from "../../components/ReportStepper";
import { StepNavigation } from "../../components/StepNavigation";
import {
  CategoryCard,
  type CategoryOption,
} from "../../components/CategoryCard";
import { LocationMap } from "../../components/LocationMap";
import { LocationSearch } from "../../components/LocationSearch";
import { SubmissionSplash } from "../../components/SubmissionSplash";
import { Dialog } from "../../components/ui/dialog";
import { DuplicateAlert } from "../../components/DuplicateAlert";
import { isLocationInFortaleza } from "@/utils/municipalityBoundaries";
import { GoogleMap, Marker } from "@react-google-maps/api";
import {
  IoAlertCircleOutline,
  IoLocationOutline,
  IoDocumentTextOutline,
  IoImageOutline,
  IoVideocamOutline,
  IoCloseCircle,
  IoCheckmarkCircle,
  IoAlert,
  IoCheckmarkDoneOutline,
  IoCreateOutline,
  IoEyeOffOutline,
} from "react-icons/io5";

import { useCreateOcurrence, useCreateAnonymousOcurrence, useGetCategories } from "@/hooks/useOcurrences";
import { useGeneratePresignedUrl } from "@/hooks/useMedia";
import { useCheckDuplicates, useFollowOcurrence } from "@/hooks/useDuplicateCheck";
import type { MediaDto } from "@/dtos/MediaDto";
import type { PotentialDuplicate } from "@/types/DuplicateOcurrence";
import { DynamicIcon } from "@/components/DynamicIcon";

// Coordenadas do centro de Frecheirinha, CE
const MUNICIPALITY_CENTER = {
  lat: -3.7588,
  lng: -40.8135,
};

const steps: Step[] = [
  {
    id: 1,
    label: "Categoria",
    icon: <IoAlertCircleOutline />,
  },
  {
    id: 2,
    label: "Local",
    icon: <IoLocationOutline />,
  },
  {
    id: 3,
    label: "Detalhes",
    icon: <IoDocumentTextOutline />,
  },
  {
    id: 4,
    label: "Revisão",
    icon: <IoCheckmarkDoneOutline />,
  },
];

const getCategoryIcon = (iconName?: string, lib?: string): React.ReactNode => {
  if (!iconName || !lib) {
    return <IoAlertCircleOutline />;
  }
  return <DynamicIcon iconName={iconName} lib={lib} />;
};



export const Report = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(
    null,
  );
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isLocationValid, setIsLocationValid] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [shouldPanMap, setShouldPanMap] = useState(false);
  const [forceUserPan, setForceUserPan] = useState(false);
  const [description, setDescription] = useState("");
  const [media, setMedia] = useState<
    Array<{ url: string; type: "image" | "video"; file: File }>
  >([]);

  const [showLocationConfirmModal, setShowLocationConfirmModal] =
    useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [potentialDuplicates, setPotentialDuplicates] = useState<PotentialDuplicate[]>([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [anonymousProtocol, setAnonymousProtocol] = useState<string | null>(null);
  const [showAnonymousSuccess, setShowAnonymousSuccess] = useState(false);
  const createOcurrence = useCreateOcurrence();
  const createAnonymousOcurrence = useCreateAnonymousOcurrence();
  const generatePresignedUrl = useGeneratePresignedUrl();
  const checkDuplicates = useCheckDuplicates();
  const followOcurrence = useFollowOcurrence();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: categoriesData } = useGetCategories();

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const categories: CategoryOption[] =
    categoriesData?.data?.map((category: any) => ({
      id: category.id.toString(),
      title: category.name,
      icon: getCategoryIcon(category.icon, category.lib),
      subcategories:
        category.subcategories?.map((sub: any) => ({
          id: sub.id.toString(),
          title: sub.name,
          description: sub.description,
        })) || [],
    })) || [];

  useEffect(() => {
    if (currentStep === 2 && !selectedLocation) {
      if (navigator.geolocation) {
        setIsLoadingLocation(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            // Verificar se a localização do usuário está dentro do município
            const isInsideMunicipality = isLocationInFortaleza(location);

            if (isInsideMunicipality) {
              // Se estiver dentro, usar a localização do usuário
              setUserLocation(location);
            } else {
              // Se estiver fora, usar o centro do município
              setUserLocation(MUNICIPALITY_CENTER);
            }

            setIsLoadingLocation(false);
          },
          (error) => {
            console.error("Erro ao obter localização:", error);
            // Em caso de erro, usar o centro do município
            setUserLocation(MUNICIPALITY_CENTER);
            setIsLoadingLocation(false);
          },
        );
      } else {
        // Se geolocalização não estiver disponível, usar o centro do município
        setUserLocation(MUNICIPALITY_CENTER);
      }
    }
  }, [currentStep]);

  const canProceed = () => {
    const selectedCategoryData = categories.find(
      (c) => c.id === selectedCategory,
    );
    const hasSubcategories =
      selectedCategoryData?.subcategories &&
      selectedCategoryData.subcategories.length > 0;

    switch (currentStep) {
      case 1:
        if (!selectedCategory) return false;
        if (hasSubcategories && !selectedSubcategory) return false;
        return true;
      case 2:
        return selectedLocation !== null && isLocationValid;
      case 3:
        return description.length >= 20 && media.length >= 1;
      case 4:
        return true; // Na revisão, sempre pode enviar
      default:
        return false;
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(-1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = async () => {
    // Se estiver no step 2 (localização), mostrar modal de confirmação
    if (currentStep === 2) {
      setShowLocationConfirmModal(true);
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === steps.length) {
      await handleSubmit();
    }
  };

  const handleConfirmLocation = async () => {
    setShowLocationConfirmModal(false);
    
    // Verificar duplicatas antes de avançar
    if (selectedLocation && selectedCategory && selectedSubcategory) {
      setIsCheckingDuplicates(true);
      
      try {
        const result = await checkDuplicates.mutateAsync({
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
          categoryId: selectedCategory,
          subcategoryId: selectedSubcategory,
        });
        
        if (result.hasPotentialDuplicates && result.duplicates.length > 0) {
          setPotentialDuplicates(result.duplicates);
          setShowDuplicateAlert(true);
          setIsCheckingDuplicates(false);
          return;
        }
      } catch (error) {
        // RN-011: Em caso de falha, prosseguir normalmente
        console.error("Erro na verificação de duplicatas:", error);
      }
      
      setIsCheckingDuplicates(false);
    }
    
    setCurrentStep(currentStep + 1);
  };

  const handleContinueAfterDuplicate = () => {
    setShowDuplicateAlert(false);
    setPotentialDuplicates([]);
    setCurrentStep(currentStep + 1);
  };

  const handleFollowOcurrence = async (occurrenceId: string) => {
    try {
      await followOcurrence.mutateAsync(occurrenceId);
      toast.success("Você agora está acompanhando esta ocorrência!");
      navigate(`/ocurrences/${occurrenceId}`);
    } catch (error) {
      toast.error("Erro ao acompanhar ocorrência. Tente novamente.");
    }
  };

  const handleLocationSelect = async (location: {
    lat: number;
    lng: number;
  }) => {
    setShouldPanMap(true);
    setSelectedLocation(location);
    setSelectedAddress("");

    // Resetar flag após um pequeno delay
    setTimeout(() => setShouldPanMap(false), 100);
  };

  const handleUseCurrentLocation = () => {
    setIsLoadingLocation(true);
    setForceUserPan(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          setSelectedLocation(location);
          setIsLoadingLocation(false);
          toast.success("Localização atual obtida com sucesso!");
          // Resetar flag após um pequeno delay
          setTimeout(() => setForceUserPan(false), 100);
        },
        (error) => {
          console.error("Erro ao obter localização:", error);
          toast.error(
            "Não foi possível obter sua localização. Verifique as permissões.",
          );
          setIsLoadingLocation(false);
          setForceUserPan(false);
        },
      );
    } else {
      toast.error("Geolocalização não é suportada pelo seu navegador.");
      setIsLoadingLocation(false);
      setForceUserPan(false);
    }
  };

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.target.files;
    if (!files) return;

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB em bytes
    const newMedia: Array<{
      url: string;
      type: "image" | "video";
      file: File;
    }> = [];
    const remainingSlots = 5 - media.length;
    const filesToProcess = Math.min(files.length, remainingSlots);

    for (let i = 0; i < filesToProcess; i++) {
      const file = files[i];

      // Validar tamanho do arquivo
      if (file.size > MAX_FILE_SIZE) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        toast.error(
          `O arquivo "${file.name}" excede o tamanho máximo de 20MB (${fileSizeMB}MB). Por favor, capture nova mídia ou selecione um arquivo menor.`,
          { duration: 5000 },
        );
        continue;
      }

      const isVideo = file.type.startsWith("video/");

      // Validar duração do vídeo
      if (isVideo) {
        const duration = await getVideoDuration(file);
        if (duration >= 11) {
          toast.error(
            `Vídeo "${file.name}" excede o limite de 10 segundos (${Math.round(duration)}s)`,
          );
          continue;
        }
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        newMedia.push({
          url: reader.result as string,
          type: isVideo ? "video" : "image",
          file,
        });
        if (newMedia.length === filesToProcess) {
          setMedia([...media, ...newMedia]);
        }
      };
      reader.readAsDataURL(file);
    }

    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = "";
  };

  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error("Erro ao carregar vídeo"));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleRemoveMedia = (index: number) => {
    setMedia(media.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (
      !selectedCategory ||
      !selectedSubcategory ||
      !selectedLocation ||
      !description
    ) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    setIsSubmitting(true);

    try {
      const address =
        selectedAddress ||
        `${selectedLocation.lat.toFixed(6)}, ${selectedLocation.lng.toFixed(6)}`;

      const uploadedMedia: MediaDto[] = [];

      for (const item of media) {
        try {
          const presignedData = await generatePresignedUrl.mutateAsync({
            fileName: item.file.name,
            mimeType: item.file.type,
            type: item.type === "image" ? "IMAGE" : "VIDEO",
            size: item.file.size,
          });

          await axios.put(presignedData.uploadUrl, item.file, {
            headers: {
              "Content-Type": item.file.type,
            },
          });

          uploadedMedia.push({
            key: presignedData.key,
            type: item.type === "image" ? "IMAGE" : "VIDEO",
            mimeType: item.file.type,
            size: item.file.size,
          });
        } catch (error) {
          console.error("Erro ao fazer upload da mídia:", error);
          throw new Error("Falha ao fazer upload das mídias");
        }
      }

      const ocurrenceData = {
        categoryId: selectedCategory,
        subcategoryId: selectedSubcategory,
        description,
        address,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        media: uploadedMedia,
      };

      if (isAnonymous) {
        const result = await createAnonymousOcurrence.mutateAsync(ocurrenceData);
        setAnonymousProtocol(result.protocol);
        setShowAnonymousSuccess(true);
      } else {
        await createOcurrence.mutateAsync(ocurrenceData);
        toast.success("Ocorrência criada com sucesso!");
        navigate("/ocurrences");
      }
    } catch (error: any) {
      console.error("Erro ao criar ocorrência:", error);
      toast.error(
        error.message || "Erro ao criar ocorrência. Tente novamente.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen pt-16 md:pt-20 pb-20 md:pb-24">
      <ReportStepper steps={steps} currentStep={currentStep} />
      <div className="w-full flex items-center justify-center mt-24 md:mt-28 lg:mt-32 px-4 lg:px-0">
        <div className="w-full max-w-4xl lg:w-[98%] lg:max-w-none">
          {currentStep === 1 && (
            <div className="space-y-5 animate-fade-in">
              {!selectedCategory ? (
                <>
                  <div>
                    <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                      Qual o tipo de problema?
                    </h1>
                    <p className="text-sm lg:text-base text-gray-600">
                      Selecione a categoria que melhor descreve o problema
                      encontrado
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {categories.map((category, index) => (
                      <motion.div
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <CategoryCard
                          category={category}
                          selected={selectedCategory === category.id}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            if (
                              !category.subcategories ||
                              category.subcategories.length === 0
                            ) {
                              setSelectedSubcategory(null);
                            }
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <button
                      onClick={() => {
                        setSelectedCategory(null);
                        setSelectedSubcategory(null);
                      }}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 mb-3 transition-colors"
                    >
                      <IoAlertCircleOutline className="text-lg" />
                      Voltar para categorias
                    </button>
                    <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                      Selecione a subcategoria
                    </h1>
                    <p className="text-sm lg:text-base text-gray-600">
                      Especifique melhor o tipo de problema
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {categories
                      .find((c) => c.id === selectedCategory)
                      ?.subcategories?.map((subcategory, index) => (
                        <motion.button
                          key={subcategory.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          onClick={() => setSelectedSubcategory(subcategory.id)}
                          className={`w-full p-4 rounded-xl flex items-start justify-between gap-3 transition-all duration-200 text-left border-2 shadow-sm ${
                            selectedSubcategory === subcategory.id
                              ? "bg-[hsl(var(--primary))]/10 border-[hsl(var(--primary))] shadow-md"
                              : "bg-white border-transparent hover:bg-gray-50 hover:border-[hsl(var(--primary))]/40"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <h3
                              className={`font-semibold text-sm mb-1 ${
                                selectedSubcategory === subcategory.id
                                  ? "text-[hsl(var(--primary))]"
                                  : "text-gray-900"
                              }`}
                            >
                              {subcategory.title}
                            </h3>
                            {subcategory.description && (
                              <p className="text-xs text-gray-600">
                                {subcategory.description}
                              </p>
                            )}
                          </div>
                          {selectedSubcategory === subcategory.id && (
                            <IoCheckmarkCircle className="text-[hsl(var(--primary))] text-xl flex-shrink-0 mt-0.5" />
                          )}
                        </motion.button>
                      ))}
                  </div>
                </>
              )}
            </div>
          )}
          {currentStep === 2 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                  Onde está o problema?
                </h1>
                <p className="text-sm lg:text-base text-gray-600">
                  Localize o problema no mapa e confirme a marcação
                </p>
              </div>

              <div className="space-y-4">
                <LocationSearch
                  onLocationSelect={handleLocationSelect}
                  onUseCurrentLocation={handleUseCurrentLocation}
                  isLoadingLocation={isLoadingLocation}
                />

                {/* Instruções de uso do mapa */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <IoLocationOutline className="text-white text-lg" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-blue-900 mb-2">
                        Como marcar a localização:
                      </h3>
                      <ul className="space-y-1.5 text-xs text-blue-800">
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold mt-0.5">
                            •
                          </span>
                          <span>
                            <strong>Clique no mapa</strong> para marcar onde
                            está o problema
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold mt-0.5">
                            •
                          </span>
                          <span>
                            <strong>Arraste</strong> para mover o mapa e
                            explorar a área
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-blue-600 font-bold mt-0.5">
                            •
                          </span>
                          <span>
                            Sua <strong>localização atual</strong> aparece
                            automaticamente como referência
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <LocationMap
                    selectedLocation={selectedLocation}
                    onLocationSelect={(location) => {
                      setSelectedLocation(location);
                      setUserLocation(null);
                      setSelectedAddress("");
                    }}
                    onAddressResolved={(address) => setSelectedAddress(address)}
                    userLocation={userLocation}
                    shouldPanToLocation={shouldPanMap}
                    forceUserLocationPan={forceUserPan}
                    onLocationValidation={(isValid) =>
                      setIsLocationValid(isValid)
                    }
                  />
                </div>

                {selectedLocation && isLocationValid && (
                  <div className="bg-[hsl(var(--primary))]/10 p-4 rounded-xl border-2 border-[hsl(var(--primary))]/30">
                    <p className="text-sm text-[hsl(var(--primary))] font-medium">
                      📍 Localização selecionada
                    </p>
                    <p className="text-xs text-[hsl(var(--primary))]/80 mt-1">
                      {selectedAddress || "Carregando endereço..."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                  Descreva o problema
                </h1>
                <p className="text-sm lg:text-base text-gray-600">
                  Forneça detalhes para agilizar o atendimento
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2 flex items-start gap-2">
                    <IoAlert className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                    <div className="flex-1 flex items-center justify-between">
                      <p className="text-xs text-yellow-800">
                        <strong>Importante:</strong> A descrição deve ter no mínimo{" "}
                        <strong>20 caracteres</strong>.
                      </p>
                      <p
                        className={`text-xs font-medium ${
                          description.length >= 20
                            ? "text-[hsl(var(--primary))]"
                            : "text-gray-400"
                        }`}
                      >
                        {description.length} caracteres
                      </p>
                    </div>
                  </div>

                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descreva o problema encontrado..."
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Adicione fotos ou vídeos
                    </h3>
                    <span
                      className={`text-sm font-semibold px-3 py-1 rounded-full ${
                        media.length === 0
                          ? "bg-red-100 text-red-700"
                          : media.length >= 5
                            ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]"
                            : "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {media.length}/5 anexos
                    </span>
                  </div>

                  {/* Aviso sobre limitação de vídeo */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3 flex items-start gap-2">
                    <IoAlert className="text-yellow-600 text-lg flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      <strong>Importante:</strong> Vídeos devem ter no máximo{" "}
                      <strong>10 segundos</strong> de duração.
                    </p>
                  </div>

                  {/* Botões de ação */}
                  {media.length < 5 && (
                    <div className="flex gap-2 mb-3">
                      {/* Input oculto para câmera */}
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*,video/*"
                        capture
                        onChange={handleMediaUpload}
                        className="hidden"
                      />

                      {/* Input oculto para galeria */}
                      <input
                        ref={galleryInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleMediaUpload}
                        className="hidden"
                      />

                      {/* Botão para câmera */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cameraInputRef.current?.click();
                        }}
                        className="flex-1 px-3 py-2 rounded-lg border-2 border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20 flex items-center justify-center gap-2 cursor-pointer transition-all duration-200"
                      >
                        <IoVideocamOutline className="text-lg text-[hsl(var(--primary))]" />
                        <span className="text-sm font-medium text-[hsl(var(--primary))]">
                          Câmera
                        </span>
                      </button>

                      {/* Botão para galeria */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          galleryInputRef.current?.click();
                        }}
                        className="flex-1 px-3 py-2 rounded-lg border-2 border-gray-300 hover:border-[hsl(var(--primary))] bg-gray-50 hover:bg-[hsl(var(--primary))]/10 flex items-center justify-center gap-2 cursor-pointer transition-all duration-200"
                      >
                        <IoImageOutline className="text-lg text-gray-600" />
                        <span className="text-sm font-medium text-gray-600">
                          Galeria
                        </span>
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-2">
                    {media.map((item, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200"
                      >
                        {item.type === "image" ? (
                          <img
                            src={item.url}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <video
                            src={item.url}
                            className="w-full h-full object-cover"
                            controls={false}
                          />
                        )}
                        {item.type === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 pointer-events-none">
                            <IoVideocamOutline className="text-white text-3xl" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveMedia(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-colors"
                        >
                          <IoCloseCircle className="text-white text-base" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {currentStep === 4 && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                  Revisar e Confirmar
                </h1>
                <p className="text-sm lg:text-base text-gray-600">
                  Confira todos os dados antes de enviar. Você pode editar
                  qualquer informação clicando em "Editar".
                </p>
              </div>

              <div className="space-y-3">
                {/* Categoria */}
                <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm border-2 border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Categoria
                      </h3>
                      <div className="flex items-start gap-2">
                        <span className="text-[hsl(var(--primary))] text-lg flex-shrink-0">
                          {
                            categories.find((c) => c.id === selectedCategory)
                              ?.icon
                          }
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800 break-words">
                            {
                              categories.find((c) => c.id === selectedCategory)
                                ?.title
                            }
                          </p>
                          {selectedSubcategory && (
                            <p className="text-xs text-gray-600 break-words">
                              {
                                categories
                                  .find((c) => c.id === selectedCategory)
                                  ?.subcategories?.find(
                                    (s) => s.id === selectedSubcategory,
                                  )?.title
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setCurrentStep(1)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20 rounded-lg transition-colors flex-shrink-0 self-start"
                    >
                      <IoCreateOutline className="text-sm" />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>

                {/* Localização */}
                <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm border-2 border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Localização
                      </h3>
                      <div className="flex items-start gap-2">
                        <IoLocationOutline className="text-[hsl(var(--primary))] text-lg flex-shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-800 break-words">
                            {selectedAddress || "Endereço não disponível"}
                          </p>
                          {selectedLocation && (
                            <p className="text-xs text-gray-500 mt-1 break-all">
                              {selectedLocation.lat.toFixed(6)},{" "}
                              {selectedLocation.lng.toFixed(6)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setCurrentStep(2)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20 rounded-lg transition-colors flex-shrink-0 self-start"
                    >
                      <IoCreateOutline className="text-sm" />
                      <span>Editar</span>
                    </button>
                  </div>
                </div>

                {/* Descrição e Mídias */}
                <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm border-2 border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Detalhes
                    </h3>
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20 rounded-lg transition-colors flex-shrink-0 self-start"
                    >
                      <IoCreateOutline className="text-sm" />
                      <span>Editar</span>
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-600 mb-1.5">
                        Descrição:
                      </p>
                      <p className="text-sm text-gray-800 break-words whitespace-pre-wrap overflow-wrap-anywhere">
                        {description}
                      </p>
                    </div>
                    {media.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-600 mb-2">
                          Mídias ({media.length}):
                        </p>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                          {media.map((item, index) => (
                            <div
                              key={index}
                              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                            >
                              {item.type === "image" ? (
                                <img
                                  src={item.url}
                                  alt={`Foto ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <>
                                  <video
                                    src={item.url}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 pointer-events-none">
                                    <IoVideocamOutline className="text-white text-xl" />
                                  </div>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>

              {/* Opção de Denúncia Anônima */}
              <div className={`rounded-xl p-3 md:p-4 shadow-sm border-2 transition-all duration-200 ${
                isAnonymous
                  ? "bg-amber-50 border-amber-300"
                  : "bg-white border-gray-100"
              }`}>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsAnonymous(!isAnonymous)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full flex-shrink-0 transition-colors duration-200 ${
                      isAnonymous ? "bg-[hsl(var(--primary))]" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        isAnonymous ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <IoEyeOffOutline className="text-gray-700 text-lg" />
                      <h3 className="text-sm font-semibold text-gray-800">
                        Fazer denúncia anônima
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Seus dados pessoais não serão vinculados a esta ocorrência
                    </p>
                  </div>
                </div>

                {isAnonymous && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <IoAlert className="text-amber-600 text-lg flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800 mb-1">
                          Atenção: Ao realizar uma denúncia anônima:
                        </p>
                        <ul className="text-xs text-amber-700 space-y-0.5">
                          <li>• Seus dados pessoais NÃO serão vinculados a esta ocorrência</li>
                          <li>• Você NÃO poderá acompanhar o status desta ocorrência</li>
                          <li>• Você NÃO poderá reabrir ou avaliar o atendimento</li>
                          <li>• O protocolo será sua única referência</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Aviso final */}
              <div className="bg-[hsl(var(--primary))]/10 border-2 border-[hsl(var(--primary))]/30 rounded-xl p-3 md:p-4 mb-24">
                <div className="flex items-start gap-3">
                  <IoCheckmarkCircle className="text-[hsl(var(--primary))] text-xl flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[hsl(var(--primary))] mb-1">
                      Pronto para enviar?
                    </p>
                    <p className="text-xs text-[hsl(var(--primary))]/80 break-words">
                      {isAnonymous
                        ? "Ao clicar em \"Enviar\", sua denúncia anônima será registrada. Seus dados pessoais não serão vinculados à ocorrência."
                        : "Ao clicar em \"Enviar\", sua ocorrência será registrada e você poderá acompanhar o status dela."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <StepNavigation
        onBack={handleBack}
        onNext={handleNext}
        nextDisabled={!canProceed() || isSubmitting}
        nextLabel={currentStep === 4 ? "Enviar" : "Próximo"}
      />

      <SubmissionSplash
        isVisible={isSubmitting}
        title={isAnonymous ? "Registrando denúncia anônima" : "Criando ocorrência"}
        message={isAnonymous ? "Estamos processando sua denúncia anônima..." : "Estamos processando sua denúncia e enviando as informações..."}
      />

      {/* Loading de Verificação de Duplicatas */}
      {isCheckingDuplicates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center max-w-xs mx-4">
            <div className="w-12 h-12 border-4 border-[hsl(var(--primary))]/20 border-t-[hsl(var(--primary))] rounded-full animate-spin mb-4" />
            <p className="text-sm font-semibold text-gray-800 text-center">
              Verificando ocorrências similares...
            </p>
            <p className="text-xs text-gray-500 mt-1 text-center">
              Isso pode levar alguns segundos
            </p>
          </div>
        </div>
      )}

      {/* Modal de Alerta de Duplicatas */}
      <DuplicateAlert
        isOpen={showDuplicateAlert}
        onClose={() => setShowDuplicateAlert(false)}
        duplicates={potentialDuplicates}
        onFollowOcurrence={handleFollowOcurrence}
        onContinueReport={handleContinueAfterDuplicate}
        isFollowing={followOcurrence.isPending}
      />

      {/* Modal de Confirmação de Localização */}
      <Dialog
        isOpen={showLocationConfirmModal}
        onClose={() => setShowLocationConfirmModal(false)}
        title="Confirmar Localização"
      >
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Por favor, confirme se a localização selecionada está correta:
          </p>

          {/* Endereço */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Endereço:</p>
            <p className="text-sm font-semibold text-gray-800">
              {selectedAddress}
            </p>
          </div>

          {/* Prévia do Mapa */}
          {selectedLocation && (
            <div className="mb-4 w-full h-[200px] rounded-lg overflow-hidden border-2 border-gray-200">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={selectedLocation}
                zoom={16}
                options={{
                  disableDefaultUI: true,
                  zoomControl: false,
                  gestureHandling: "none",
                  styles: [
                    {
                      featureType: "poi",
                      elementType: "labels",
                      stylers: [{ visibility: "off" }],
                    },
                  ],
                }}
              >
                <Marker position={selectedLocation} />
              </GoogleMap>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowLocationConfirmModal(false)}
              className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Alterar
            </button>
            <button
              onClick={handleConfirmLocation}
              className="flex-1 px-4 py-3 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-medium rounded-xl transition-colors"
            >
              Confirmar
            </button>
          </div>
        </div>
      </Dialog>

      {/* Modal de Sucesso - Denúncia Anônima */}
      <Dialog
        isOpen={showAnonymousSuccess}
        onClose={() => {
          setShowAnonymousSuccess(false);
          navigate("/home");
        }}
        title="Denúncia Registrada"
      >
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <IoCheckmarkCircle className="text-green-600 text-4xl" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            Denúncia anônima registrada com sucesso!
          </h3>
          {anonymousProtocol && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 mb-1">Protocolo</p>
              <p className="text-2xl font-bold text-[hsl(var(--primary))]">
                {anonymousProtocol}
              </p>
            </div>
          )}
          <p className="text-sm text-gray-600 mb-6">
            Anote este protocolo. Como esta é uma denúncia anônima, não será
            possível acompanhar o andamento.
          </p>
          <button
            onClick={() => {
              setShowAnonymousSuccess(false);
              navigate("/home");
            }}
            className="w-full px-4 py-3 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white font-semibold rounded-xl transition-colors"
          >
            Entendi
          </button>
        </div>
      </Dialog>
    </div>
  );
};
